import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Equipment {
  id: string
  asset_tag: string
  name: string
  category: string
  manufacturer: string
  model: string
  status: string
  condition: string
  location: string
  purchase_cost: number
}

interface Assignment {
  id: string
  equipment_id: string
  assigned_to: string
  department: string
  assigned_date: string
  status: string
}

export default function EquipmentPage() {
  const [equipment, setEquipment] = useState<Equipment[]>([])
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [eqRes, assRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/equipment/'),
      fetch(API_BASE + '/equipment/assignments'),
      fetch(API_BASE + '/equipment/statistics')
    ])
    if (eqRes.ok) setEquipment((await eqRes.json()).equipment || [])
    if (assRes.ok) setAssignments((await assRes.json()).assignments || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': case 'active': return 'bg-green-100 text-green-800'
      case 'assigned': return 'bg-blue-100 text-blue-800'
      case 'in_repair': case 'returned': return 'bg-yellow-100 text-yellow-800'
      case 'retired': case 'lost': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent': return 'text-green-600'
      case 'good': return 'text-blue-600'
      case 'fair': return 'text-yellow-600'
      case 'poor': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Equipment Tracking</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Equipment</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Equipment</div>
              <div className="text-2xl font-bold">{statistics.total_equipment}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Available</div>
              <div className="text-2xl font-bold text-green-600">{statistics.available}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Assigned</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.assigned}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Repair</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.in_repair}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Value</div>
              <div className="text-2xl font-bold">${statistics.total_asset_value?.toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Equipment Inventory</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Condition</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {equipment.slice(0, 12).map(eq => (
                  <tr key={eq.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{eq.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{eq.asset_tag}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{eq.manufacturer} {eq.model}</div>
                      <div className="text-xs text-gray-500 capitalize">{eq.category} - {eq.location}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm capitalize ${getConditionColor(eq.condition)}`}>{eq.condition}</span>
                      <div className="text-xs text-gray-500">${eq.purchase_cost?.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(eq.status)}`}>{eq.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Assignments</h3>
            <div className="space-y-3">
              {assignments.filter(a => a.status === 'active').slice(0, 10).map(ass => (
                <div key={ass.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{ass.assigned_to}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(ass.status)}`}>{ass.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{ass.department}</div>
                  <div className="text-xs text-gray-500 mt-1">Since: {ass.assigned_date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
